import type { MailboxStatusView } from '@fos/shared/contracts/mail';
import { mailAuthLostTitle } from '@fos/shared/contracts/mail';
import { RefreshCw } from 'lucide-react';
import { errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { ContextBanner } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Skeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { useMailRefresh, useMailStatus } from '../hooks';
import { lastSyncText } from '../mail-format';

/**
 * Baner problemu skrzynki (MVP §9: „utrata autoryzacji daje widoczny błąd”): utracona autoryzacja → „Utracono dostęp
 * do skrzynki …” z linkiem do Ustawień → Integracje; inny błąd ostatniej synchronizacji → jego opis.
 */
export function MailboxBanner({ mailbox, className }: { mailbox: MailboxStatusView; className?: string }) {
  if (mailbox.authLost) {
    return (
      <ContextBanner tone="warning" linkTo="/ustawienia/integracje" linkLabel="Połącz ponownie" className={className}>
        {mailAuthLostTitle(mailbox.address)}. Nowe maile nie są pobierane, a wysyłka jest wstrzymana.
      </ContextBanner>
    );
  }
  if (mailbox.status === 'error' && mailbox.lastError) {
    return (
      <ContextBanner tone="warning" className={className}>
        Ostatnia synchronizacja {mailbox.address} nie powiodła się: {mailbox.lastError}
      </ContextBanner>
    );
  }
  return null;
}

/** Banery problemów wszystkich skrzynek (pełna szerokość pod nagłówkiem strony; bez problemu = nic). */
export function MailboxBanners({ className }: { className?: string }) {
  const query = useMailStatus();
  const list = (query.data?.mailboxes ?? []).filter(
    (m) => m.authLost || (m.status === 'error' && Boolean(m.lastError)),
  );
  if (list.length === 0) return null;
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {list.map((m) => (
        <MailboxBanner key={m.id} mailbox={m} />
      ))}
    </div>
  );
}

/**
 * Stan skrzynek: adres, „Ostatnia synchronizacja: 2 min temu”, „Symulacja” (adapter fake), „Synchronizuj teraz”
 * (zadanie w workerze) i baner problemu. `compact` = jedna linia nad listą wiadomości; `banners={false}` = bez
 * banerów (strona pokazuje je osobno pod nagłówkiem: `MailboxBanners`).
 */
export function MailboxStatus({
  compact = false,
  banners = true,
  className,
}: {
  compact?: boolean;
  banners?: boolean;
  className?: string;
}) {
  const query = useMailStatus();
  const refresh = useMailRefresh();
  const syncNow = trpc.mail.syncNow.useMutation({
    onSuccess: (res) => {
      toast.success(res.queued > 0 ? 'Synchronizacja w toku. Nowe maile pojawią się za chwilę.' : 'Brak skrzynek.');
      window.setTimeout(() => void refresh(), 4000);
    },
    onError: (err) => toast.error(errorMessage(err, 'Nie udało się uruchomić synchronizacji.')),
  });

  if (query.isPending) return <Skeleton className={cn('h-5 w-64', className)} />;
  if (query.isError) {
    return (
      <p className={cn('text-caption text-fg-muted', className)}>
        Nie udało się wczytać stanu skrzynki.{' '}
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="rounded-xs text-accent underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          Spróbuj ponownie
        </button>
      </p>
    );
  }
  const { mailboxes, canSync } = query.data;
  if (mailboxes.length === 0) {
    return compact ? null : (
      <ContextBanner tone="info" linkTo="/ustawienia/integracje" linkLabel="Ustawienia" className={className}>
        Brak podłączonej skrzynki. Poczta pojawi się po podłączeniu skrzynki w Ustawieniach → Integracje.
      </ContextBanner>
    );
  }
  const now = new Date();
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {mailboxes.map((m) => (
        <div key={m.id} className="flex flex-col gap-2">
          {banners && <MailboxBanner mailbox={m} />}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-fg-muted">
            {!compact && <span className="font-medium text-fg-secondary">{m.address}</span>}
            <span>{lastSyncText(m, now)}</span>
            {m.simulated && <Status tone="warning">Symulacja</Status>}
            {canSync && (
              <Button
                variant="ghost"
                size="s"
                icon={RefreshCw}
                loading={syncNow.isPending && syncNow.variables?.mailboxId === m.id}
                onClick={() => syncNow.mutate({ mailboxId: m.id })}
                className={TOUCH_BUTTON}
              >
                Synchronizuj teraz
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
